import React, { useEffect, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import {
  List, Typography, Button,
} from 'antd';
import {
  number, func, shape, string, oneOfType, arrayOf, bool,
} from 'prop-types';
import FileIcon from 'components/file-icon';
import ErrorsAlert from 'components/errors-alert/ErrorsAlert';

const { Link, Text } = Typography;

const THUMBNAIL_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']);
const THUMBNAIL_TYPES = new Set([
  'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
]);

const THUMBNAIL_SIZE = 40;

const fileExtension = (file) => {
  if (file.extension) return file.extension.toLowerCase();

  const nameParts = file.name.split('.');
  return nameParts.length >= 2 ? nameParts.at(-1).toLowerCase() : null;
};

const isImageFile = (file, extension) => (
  THUMBNAIL_EXTENSIONS.has(extension) || THUMBNAIL_TYPES.has(file.type)
);

const localImageBlob = (file) => {
  if (file.originFileObj instanceof Blob) return file.originFileObj;
  if (file instanceof Blob) return file;
  return null;
};

const TicketAttachmentListItem = ({
  file,
  hideItem,
  errorColor = '#CC0000',
  errors = [],
  clearErrors,
  isCurrentUserAuthor = true,
}) => {
  const extension = fileExtension(file);
  const sizeMb = file.size / 1024 / 1024 || 0;
  const formattedSize = `${sizeMb.toFixed(2)} Mb`;

  const titleContent = (
    <>
      {file.name}
      <Text type="secondary">
        {' | '}
        {[extension, formattedSize].filter(Boolean).join(' | ')}
      </Text>
    </>
  );

  const title = (() => {
    if (errors.length) return <Text type="danger">{titleContent}</Text>;
    if (file.url) return <Link href={file.url} target="_blank">{titleContent}</Link>;
    return <Text>{titleContent}</Text>;
  })();

  const description = errors.length
    ? <ErrorsAlert errors={errors} clearErrors={clearErrors} />
    : null;

  const isImage = isImageFile(file, extension);

  const localPreview = useMemo(() => {
    if (file.url || !isImage) return null;

    const blob = localImageBlob(file);
    return blob ? URL.createObjectURL(blob) : null;
  }, [file, isImage]);

  useEffect(() => () => {
    if (localPreview) URL.revokeObjectURL(localPreview);
  }, [localPreview]);

  const previewSrc = file.url || localPreview;

  const avatar = isImage && previewSrc ? (
    <img
      className="ticket-attachment__thumbnail"
      src={previewSrc}
      alt={file.name}
      width={THUMBNAIL_SIZE}
      height={THUMBNAIL_SIZE}
      style={{ objectFit: 'cover', borderRadius: 4 }}
    />
  ) : (
    <FileIcon
      format={extension}
      status={file.status === 'done' ? null : file.status}
      style={{
        fontSize: 30,
        color: file.status === 'error' ? errorColor : '#555',
      }}
    />
  );

  const deleteAction = isCurrentUserAuthor ? [
    <Button
      type="link"
      style={{ padding: 0 }}
      onClick={hideItem}
    >
      <DeleteOutlined style={{ color: errorColor }} />
    </Button>,
  ] : [];

  return (
    <List.Item actions={deleteAction}>
      <List.Item.Meta
        avatar={avatar}
        title={title}
        description={description}
      />
    </List.Item>
  );
};

TicketAttachmentListItem.propTypes = {
  file: shape({
    url: string,
    name: string,
    uid: oneOfType([number, string]),
    status: string,
    size: number,
    extension: string,
    type: string,
    id: number,
  }).isRequired,
  hideItem: func.isRequired,
  clearErrors: func.isRequired,
  errorColor: string,
  errors: arrayOf(shape({})),
  isCurrentUserAuthor: bool,
};

export { TicketAttachmentListItem };
